/**
 * EmployeeSignaturePanel
 *
 * One employee's personal e-signature (PNG) -- settable either by the employee themselves
 * (mode="self", used from the account settings page every signed-in user has) or by HR on
 * an employee's record (mode="admin", used from the Employee view/edit pages). The signature
 * is general-purpose: it is usable anywhere the system needs to stamp whoever actually
 * carried out an action -- the Leave application form PDF is the first consumer, but it is
 * not specific to Leave or any one workflow/module. Replaces the old department-level "Head
 * of Department" signature, which was one signature shared by a whole department rather than
 * belonging to the person who actually acted.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, Trash2, Upload } from "lucide-react";

import { useToast } from "#/components/common/ToastProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import {
  clearEmployeeSignature,
  fetchEmployeeSignatureInfo,
  fetchEmployeeSignaturePreview,
  uploadEmployeeSignature,
  type EmployeeSignatureInfo,
} from "../api";

type Props =
  | { mode: "self" }
  | { mode: "admin"; employeeId: number; canEdit: boolean };

export function EmployeeSignaturePanel(props: Props) {
  const target: number | "me" = props.mode === "self" ? "me" : props.employeeId;
  const canEdit = props.mode === "self" ? true : props.canEdit;

  const toast = useToast();
  const confirm = useConfirm();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);

  const [info, setInfo] = useState<EmployeeSignatureInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    try {
      const data = await fetchEmployeeSignatureInfo(target);
      setInfo(data);
    } catch {
      setInfo(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  useEffect(() => {
    let cancelled = false;
    if (info?.hasSignature) {
      fetchEmployeeSignaturePreview(target).then((blob) => {
        if (cancelled) return;
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        setPreviewUrl(url);
      }).catch(() => { /* no preview -- controls still work */ });
    } else {
      setPreviewUrl(null);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, info?.hasSignature]);

  useEffect(() => {
    return () => { if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current); };
  }, []);

  async function handleFile(file: File) {
    if (file.type !== "image/png") {
      toast.error("Please upload a PNG image (transparent background recommended).");
      return;
    }
    setBusy(true);
    try {
      await uploadEmployeeSignature(target, file);
      toast.success("Signature saved.");
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to upload signature.");
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleClear() {
    if (!(await confirm("Remove this signature? It will no longer be stamped on new approvals."))) return;
    setBusy(true);
    try {
      await clearEmployeeSignature(target);
      toast.success("Signature removed.");
      await reload();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to remove signature.");
    } finally {
      setBusy(false);
    }
  }

  // Deliberately unwrapped (no own card/border) -- both call sites already place this
  // inside their own section wrapper with its own title and icon (EmployeeViewPage's
  // <Section>, AccountSecurityPage's <section>), matching how EmployeeFamilyPanel and the
  // other nested panels on those pages behave.
  return (
    <div>
      <p className="text-xs text-slate-500">
        {props.mode === "self"
          ? "Upload your own signature (PNG). It will be stamped automatically wherever you approve or sign off on something in the system."
          : "Upload this employee's signature (PNG) on their behalf. It will be stamped automatically wherever they approve or sign off on something in the system."}
      </p>

      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Signature preview"
              className="h-12 max-w-[160px] rounded-lg border bg-white object-contain p-1"
            />
          )}

          {canEdit ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                }}
              />
              <button
                type="button"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {info?.hasSignature ? "Replace signature" : "Upload signature"}
              </button>
              {info?.hasSignature && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleClear}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                  Remove
                </button>
              )}
            </>
          ) : (
            !info?.hasSignature && (
              <span className="text-xs text-slate-400">No signature on file.</span>
            )
          )}
        </div>
      )}
    </div>
  );
}
